import { useEffect, useRef } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import type { FilterDimension, ChartMode } from '../../type/dynamicChart';
import { filterDimensionRegistry } from '../../utils/filterDimensions';

type FilterDimensionPickerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (dim: FilterDimension) => void;
  mode?: ChartMode;
  children: React.ReactNode; // the trigger element
};

export function FilterDimensionPicker({
  open,
  onOpenChange,
  onSelect,
  mode,
  children,
}: FilterDimensionPickerProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onOpenChange(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, onOpenChange]);

  const categoricalDims = filterDimensionRegistry.filter(
    (d) => d.kind === 'categorical' && (!d.comparisonOnly || mode === 'comparison')
  );
  const numericDims = filterDimensionRegistry.filter((d) => d.kind === 'numeric');
  const dateDims = filterDimensionRegistry.filter((d) => d.kind === 'date');

  const handleSelect = (dim: FilterDimension) => {
    onSelect(dim);
    onOpenChange(false);
  };

  return (
    <div ref={containerRef} className="relative inline-block">
      {/* Trigger slot */}
      <div onClick={() => onOpenChange(!open)}>{children}</div>

      {/* Dropdown */}
      {open && (
        <div className="absolute bottom-full left-0 z-50 mb-1 min-w-[240px] rounded-lg border border-border bg-card shadow-lg">
          <div className="max-h-72 overflow-y-auto p-1">
            {/* Categorical section */}
            <div className="eyebrow mb-1 px-2 py-1">
              {t('analytics.dashboard.complexCharts.filters.sectionCategorical')}
            </div>
            {categoricalDims.map((d) => (
              <button
                key={d.key}
                type="button"
                onClick={() => handleSelect(d.key)}
                className="flex w-full flex-col rounded px-2 py-1.5 text-left outline-none hover:bg-muted/60 focus-visible:ring-[3px] focus-visible:ring-ring/50"
              >
                <span className="text-xs font-medium text-foreground">{t(d.labelKey)}</span>
                <span className="font-mono text-xs text-muted-foreground">{d.key}</span>
              </button>
            ))}

            {/* Numeric section */}
            <div className="eyebrow mb-1 mt-2 px-2 py-1">
              {t('analytics.dashboard.complexCharts.filters.sectionNumeric')}
            </div>
            {numericDims.map((d) => (
              <button
                key={d.key}
                type="button"
                onClick={() => handleSelect(d.key)}
                className="flex w-full flex-col rounded px-2 py-1.5 text-left outline-none hover:bg-muted/60 focus-visible:ring-[3px] focus-visible:ring-ring/50"
              >
                <span className="text-xs font-medium text-foreground">{t(d.labelKey)}</span>
                <span className="font-mono text-xs text-muted-foreground">
                  {d.key}
                  {d.unit ? ` (${d.unit})` : ''}
                </span>
              </button>
            ))}

            {/* Date section */}
            <div className="eyebrow mb-1 mt-2 px-2 py-1">
              {t('analytics.dashboard.complexCharts.filters.sectionDate')}
            </div>
            {dateDims.map((d) => (
              <button
                key={d.key}
                type="button"
                onClick={() => handleSelect(d.key)}
                className="flex w-full flex-col rounded px-2 py-1.5 text-left outline-none hover:bg-muted/60 focus-visible:ring-[3px] focus-visible:ring-ring/50"
              >
                <span className="text-xs font-medium text-foreground">{t(d.labelKey)}</span>
                <span className="font-mono text-xs text-muted-foreground">{d.key}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
